import type {
  HomePasosBannerRow,
  HomePasosContent,
  HomePasosItem,
} from '../models/home-pasos-content.model';
import { preparePasosTitleHtml } from './home-pasos-html.util';

/** Asset único de fallback (desktop; se usa también en mobile). */
export const PASOS_IMAGE_DESKTOP_FALLBACK = 'assets/img/Home/pasos/mockup-hand-phone.png';

/** Orden del registro de imagen desktop. */
export const PASOS_ORDEN_IMAGEN = 5;

function normalizeOrden(row: HomePasosBannerRow, index: number): number {
  const orden = row.orden;
  return typeof orden === 'number' && !Number.isNaN(orden) ? orden : index;
}

function mapPasoRow(
  row: HomePasosBannerRow,
  fallbackItem: HomePasosItem | undefined
): HomePasosItem | null {
  const title = row.titulo?.trim();
  const description = row.contenido?.trim();

  if (!title && !description) {
    return fallbackItem ?? null;
  }

  return {
    title: title || fallbackItem?.title || '',
    description: description || fallbackItem?.description || '',
  };
}

/**
 * Mapeo — registros `PasosCredito` ordenados ASC:
 * - `orden` 0 → cabecera: `titulo` eyebrow, `nombre_boton` título HTML
 * - `orden` 1–4 → pasos: `titulo` nombre, `contenido` descripción
 * - `orden` 5 → imagen desktop: `urlArchivo`
 */
export function mapPasosBannersToContent(
  rows: HomePasosBannerRow[],
  fallback: HomePasosContent
): HomePasosContent {
  if (!rows?.length) {
    return fallback;
  }

  const sorted = [...rows].sort(
    (a, b) => normalizeOrden(a, 0) - normalizeOrden(b, 0)
  );

  const headerRow = sorted.find((row) => normalizeOrden(row, -1) === 0) ?? sorted[0];
  const pasoRows = sorted.filter((row) => {
    const orden = normalizeOrden(row, -1);
    return orden >= 1 && orden <= 4;
  });
  const imageRow = sorted.find((row) => normalizeOrden(row, -1) === PASOS_ORDEN_IMAGEN);

  const eyebrowRaw = headerRow?.titulo?.trim();
  const titleRaw = headerRow?.nombreBoton?.trim();
  const imageUrl = imageRow?.urlArchivo?.trim();

  const items: HomePasosItem[] = [];
  for (let orden = 1; orden <= 4; orden++) {
    const row = pasoRows.find((r) => normalizeOrden(r, -1) === orden);
    const fallbackItem = fallback.items[orden - 1];
    const mapped = row ? mapPasoRow(row, fallbackItem) : fallbackItem;
    if (mapped) {
      items.push(mapped);
    }
  }

  if (!eyebrowRaw && !titleRaw && items.length === 0 && !imageUrl) {
    return fallback;
  }

  return {
    eyebrow: eyebrowRaw || fallback.eyebrow,
    titleHtml: titleRaw ? preparePasosTitleHtml(titleRaw) : fallback.titleHtml,
    items: items.length ? items : fallback.items,
    imageDesktopSrc: imageUrl || fallback.imageDesktopSrc,
  };
}
